import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import Card from '../../../shared/components/Card';
import { getApiBaseUrl } from '../../../core/config/api';
import { useInterventionsList } from './hooks/useInterventionsList';
import { submitInterventionScenario } from './hooks/submitInterventionScenario';
import VehiculesByServicePicker from './hooks/VehiculesByServicePicker';
import InterventionParamsSection from './hooks/InterventionParamsSection';
import { useInterventionFormData } from './hooks/useInterventionFormData';
import configService from '../../../shared/services/configService';
import type { Service, VehiculeConfig } from '../../../shared/types/config';

export interface CategorieMission {
  id: number;
  nom: string;
  label?: string;
}

/** Conditions d’unlock définies sur le scénario (`notes_cta.panel_meta.conditions_spec`). */
export interface InterventionConditionsSpec {
  batiments: { config_building_id: number; quantite_min: number }[];
  /** Réservé rétrocompat / anciennes données ; le formulaire n’envoie plus de compétences ici. */
  competence_ids?: number[];
}

/** Réponse GET /api/cta/interventions/:id pour préremplir le formulaire */
export interface PanelInterventionDetail {
  id: number;
  service_id: number;
  titres: string[];
  types_mission: number[];
  /** Ancien mode : IDs `cta_conditions` (optionnel si tout est en conditions_spec). */
  conditions: number[];
  conditions_spec: InterventionConditionsSpec;
  statut: string;
  is_tuto: boolean;
  is_exceptionnelle: boolean;
  params: {
    temps_max: number;
    victimes_min: number;
    victimes_max: number;
    hostiles_min: number;
    hostiles_max: number;
    vehicules_accidentes_min: number;
    vehicules_accidentes_max: number;
  };
  recompense: { xp: number; credits: number };
  evolutions: { id: number; taux: number }[];
  vehicules_requis: { id_vehicule: number; quantite: number }[];
  memo?: string;
}

const InterventionsCreate: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: editIdParam } = useParams<{ id: string }>();
  const isEmbedded = new URLSearchParams(location.search).get('embed') === '1';
  const editingId = editIdParam ? parseInt(editIdParam, 10) : NaN;
  const isEditMode = Number.isInteger(editingId) && editingId > 0;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [conditionBatimentRows, setConditionBatimentRows] = useState<
    { config_building_id: number; quantite_min: number }[]
  >([{ config_building_id: 0, quantite_min: 1 }]);
  /** id d'intervention → taux d'évolution en % (0-100) */
  const [evolutionTaux, setEvolutionTaux] = useState<Record<number, number>>({});
  const [openServiceId, setOpenServiceId] = useState<number | null>(null);
  const [vehiculeSearchQuery, setVehiculeSearchQuery] = useState('');
  /** id véhicule (config) → quantité requise pour cette intervention (défaut 0) */
  const [vehiculeQuantites, setVehiculeQuantites] = useState<Record<number, number>>({});
  /** Tutoriel et exceptionnelle sont mutuellement exclusifs */
  const [scenarioKind, setScenarioKind] = useState<'aucun' | 'tuto' | 'exceptionnelle'>('aucun');

  const {
    categoriesMission,
    loadingOptions,
    configBuildingTypes,
    services,
    vehicules,
    loadingVehicules,
    panelEditDetail,
    loadingEditDetail,
    detailLoadError,
  } = useInterventionFormData({
    isEditMode,
    editingId,
    setEvolutionTaux,
    setVehiculeQuantites,
    setScenarioKind,
    setConditionBatimentRows,
  });

  /** Ancien mode `cta_conditions` (IDs) — exposé pour le JSX / HMR si référencé. */
  const conditions = useMemo(
    () => (panelEditDetail?.conditions?.length ? [...panelEditDetail.conditions] : []),
    [panelEditDetail]
  );
  const { interventions: interventionsList, loading: loadingInterventions, reload: reloadInterventions } =
    useInterventionsList();
  const formRef = useRef<HTMLFormElement>(null);

  const d = isEditMode ? panelEditDetail : null;
  const formMountKey = isEditMode
    ? `edit-${editingId}-${d?.id ?? 0}-c${categoriesMission.length}-b${configBuildingTypes.length}-i${loadingInterventions ? 0 : interventionsList.length}`
    : 'create';

  const interventionsForEvolutions = useMemo(
    () => interventionsList.filter((inter) => !isEditMode || inter.id !== editingId),
    [interventionsList, isEditMode, editingId]
  );

  const canShowInterventionForm =
    !isEditMode || (!!panelEditDetail && !loadingEditDetail && !detailLoadError);

  const setVehiculeQuantite = (vehiculeId: number, quantite: number) => {
    setVehiculeQuantites((prev) => ({ ...prev, [vehiculeId]: Math.max(0, quantite) }));
  };

  /** Par service : véhicules groupés par type, triés par nom (alpha) */
  const vehiculesByService = useMemo(() => {
    return services.map((service) => {
      const list = (vehicules.filter((v) => v.id_service === service.id) as VehiculeConfig[])
        .slice()
        .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
      const byType: Record<string, VehiculeConfig[]> = {};
      list.forEach((v) => {
        const t = v.type || 'Autre';
        if (!byType[t]) byType[t] = [];
        byType[t].push(v);
      });
      const types = Object.keys(byType).sort((a, b) => a.localeCompare(b, 'fr'));
      return { service, byType, types };
    });
  }, [services, vehicules]);

  const setEvolutionTauxFor = (id: number, taux: number) => {
    setEvolutionTaux((prev) => ({ ...prev, [id]: Math.min(100, Math.max(0, taux)) }));
  };

  const toggleEvolution = (id: number, checked: boolean) => {
    setEvolutionTaux((prev) => ({ ...prev, [id]: checked ? 50 : 0 }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData(e.currentTarget);
    const result = await submitInterventionScenario({
      formData,
      scenarioKind,
      conditionBatimentRows,
      vehiculeQuantites,
      evolutionTaux,
      isEditMode,
      editingId,
      panelEditDetail,
      conditions,
    });

    if (result.success) {
      setSuccess(result.message ?? null);
      if (isEditMode) {
        void reloadInterventions();
      } else {
        formRef.current?.reset();
        setEvolutionTaux({});
        setVehiculeQuantites({});
        setScenarioKind('aucun');
        setOpenServiceId(null);
        setVehiculeSearchQuery('');
        setConditionBatimentRows([{ config_building_id: 0, quantite_min: 1 }]);
        void reloadInterventions();
      }
    } else {
      setError(result.error ?? 'Erreur inconnue.');
    }
    setLoading(false);
  };

  return (
    <div className={isEmbedded ? 'h-full overflow-y-auto bg-[#0a1329]' : 'min-h-screen relative overflow-hidden'}>
      {!isEmbedded && (
        <>
          <BackgroundImage />
          <ParticlesBackground />
          <Navbar />
        </>
      )}
      <div className={isEmbedded ? 'px-4 py-4' : 'relative z-10 pt-24 pb-16'}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center justify-between ${isEmbedded ? 'mb-4' : 'mb-8'}`}>
            <div>
              <h1 className={`${isEmbedded ? 'text-2xl' : 'text-5xl'} font-bold text-white mb-2 text-gradient`}>
                {isEditMode ? 'Modifier le scénario d\'intervention' : 'Créer un scénario d\'intervention'}
              </h1>
              <p className="text-gray-300">
                {isEditMode
                  ? `Scénario #${editingId} — mettez à jour les champs puis enregistrez.`
                  : 'Créez un nouveau scénario d\'intervention CTA'}
              </p>
            </div>
            {!isEmbedded && (
              <button
                onClick={() => navigate('/panel')}
                className="px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Retour</span>
              </button>
            )}
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-200">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-500/20 border border-green-500 rounded-lg text-green-200">
              {success}
            </div>
          )}

          {isEditMode && loadingEditDetail && !detailLoadError && (
            <Card className="p-6 mb-4">
              <p className="text-gray-300">Chargement du scénario...</p>
            </Card>
          )}

          {isEditMode && detailLoadError && (
            <Card className="p-6 mb-4">
              <p className="text-red-300 mb-4">{detailLoadError}</p>
              <button
                type="button"
                onClick={() => navigate('/panel/interventions/manage')}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
              >
                Retour à la liste des scénarios
              </button>
            </Card>
          )}

          {canShowInterventionForm && (
          <Card className="p-6">
            <form key={formMountKey} ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Titre *
                  </label>
                  <input
                    type="text"
                    name="titre"
                    required
                    defaultValue={d?.titres?.[0] ?? ''}
                    className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Titre de l'intervention"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Catégorie de mission
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Cette catégorie sert à classifier le scénario côté CTA (filtrage, tri et lecture rapide des scénarios par thème).
                  </p>
                  <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-700 bg-gray-800/50 p-3">
                    {loadingOptions ? (
                      <p className="text-sm text-gray-500">Chargement...</p>
                    ) : categoriesMission.length === 0 ? (
                      <p className="text-sm text-gray-500">Aucune catégorie. Créez-en dans la base de données.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {categoriesMission.map((item) => (
                          <label key={item.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-700/30 rounded px-2 py-1.5">
                            <input
                              type="checkbox"
                              name="types_mission"
                              value={item.id}
                              defaultChecked={d?.types_mission?.includes(item.id) ?? false}
                              className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-primary-600 focus:ring-2 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-200">{item.nom || item.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="md:col-span-2 rounded-lg border border-gray-700 bg-gray-800/40 p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Conditions de déblocage</label>
                    <p className="text-xs text-gray-500 mb-3">
                      Types de bâtiments requis (quantité minimale par type). Les quantités de véhicules pour la mission
                      se règlent dans la section « Véhicules par service » ci-dessous — les compétences du personnel /
                      armement des engins restent hors intervention.
                    </p>
                    <p className="text-xs text-blue-300/90 mb-3">
                      Conseil gameplay : définissez au moins un minimum de véhicules côté mission (ex: 1 véhicule pour le tutoriel)
                      afin de débloquer le premier appel proprement.
                    </p>
                    {loadingOptions ? (
                      <p className="text-sm text-gray-500">Chargement des types de bâtiments…</p>
                    ) : (
                      <>
                        <p className="text-xs font-medium text-gray-400 mb-2">Bâtiments (quantité minimale par type)</p>
                        <div className="space-y-2">
                          {conditionBatimentRows.map((row, idx) => (
                            <div key={idx} className="flex flex-wrap items-center gap-2">
                              <select
                                value={row.config_building_id || ''}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value, 10) || 0;
                                  setConditionBatimentRows((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], config_building_id: v };
                                    return next;
                                  });
                                }}
                                className="flex-1 min-w-[200px] px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
                              >
                                <option value="">— Type de bâtiment —</option>
                                {configBuildingTypes.map((bt) => (
                                  <option key={bt.id} value={bt.id}>
                                    {bt.nom}
                                    {bt.code ? ` (${bt.code})` : ''}
                                  </option>
                                ))}
                              </select>
                              <label className="flex items-center gap-1 text-sm text-gray-400">
                                min.
                                <input
                                  type="number"
                                  min={1}
                                  value={row.quantite_min}
                                  onChange={(e) => {
                                    const q = parseInt(e.target.value, 10) || 1;
                                    setConditionBatimentRows((prev) => {
                                      const next = [...prev];
                                      next[idx] = { ...next[idx], quantite_min: Math.max(1, q) };
                                      return next;
                                    });
                                  }}
                                  className="w-16 px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                                />
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  setConditionBatimentRows((prev) => prev.filter((_, i) => i !== idx))
                                }
                                disabled={conditionBatimentRows.length <= 1}
                                className="text-xs text-red-400 hover:text-red-300 disabled:opacity-40 px-2"
                              >
                                Retirer
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setConditionBatimentRows((prev) => [
                              ...prev,
                              { config_building_id: 0, quantite_min: 1 },
                            ])
                          }
                          className="mt-2 text-sm text-primary-400 hover:text-primary-300"
                        >
                          + Ajouter un type de bâtiment
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Évolutions de mission
                  </label>
                  <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-700 bg-gray-800/50 p-3">
                    {loadingInterventions ? (
                      <p className="text-sm text-gray-500">Chargement de la liste des interventions...</p>
                    ) : interventionsForEvolutions.length === 0 ? (
                      <p className="text-sm text-gray-500">Aucune intervention existante. Créez d’abord des scénarios (liste du panel) pour pouvoir les choisir comme évolution.</p>
                    ) : (
                      <div className="space-y-2">
                        {interventionsForEvolutions.map((inter) => {
                          const checked = (evolutionTaux[inter.id] ?? 0) > 0;
                          const taux = evolutionTaux[inter.id] ?? 50;
                          return (
                            <div key={inter.id} className="flex items-center gap-3 rounded px-2 py-1.5 hover:bg-gray-700/30">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => toggleEvolution(inter.id, e.target.checked)}
                                className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-primary-600 focus:ring-2 focus:ring-primary-500 shrink-0"
                              />
                              <span className="text-sm text-gray-200 flex-1 min-w-0 truncate">
                                {inter.titres?.[0] || inter.categorie || `Intervention #${inter.id}`}
                              </span>
                              {checked && (
                                <div className="flex items-center gap-1 shrink-0">
                                  <input
                                    type="number"
                                    min={1}
                                    max={100}
                                    value={taux}
                                    onChange={(e) => setEvolutionTauxFor(inter.id, parseInt(e.target.value, 10) || 0)}
                                    className="w-14 px-2 py-1 text-sm bg-gray-800/50 border border-gray-600 rounded text-white focus:ring-1 focus:ring-primary-500"
                                  />
                                  <span className="text-xs text-gray-400">%</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Cochez les scénarios vers lesquels cette intervention peut évoluer et indiquez le taux de chance (%).</p>
                </div>

                <VehiculesByServicePicker
                  loadingVehicules={loadingVehicules}
                  vehiculesByService={vehiculesByService}
                  vehiculeSearchQuery={vehiculeSearchQuery}
                  setVehiculeSearchQuery={setVehiculeSearchQuery}
                  openServiceId={openServiceId}
                  setOpenServiceId={setOpenServiceId}
                  vehiculeQuantites={vehiculeQuantites}
                  setVehiculeQuantite={setVehiculeQuantite}
                />

                <InterventionParamsSection
                  d={d}
                  scenarioKind={scenarioKind}
                  setScenarioKind={setScenarioKind}
                />
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                {!isEmbedded && (
                  <button
                    type="button"
                    onClick={() => navigate('/panel')}
                    className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    Annuler
                  </button>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-gradient-to-r from-primary-600 to-purple-600 hover:from-primary-700 hover:to-purple-700 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (isEditMode ? 'Enregistrement...' : 'Création...') : isEditMode ? 'Enregistrer les modifications' : 'Créer le scénario'}
                </button>
              </div>
            </form>
          </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterventionsCreate;

